import { AddOrEditMatterLitigationDatePlanModal } from "@/components/features/settings/defaults/matter-litigation-date-plans/AddOrEditMatterLitigationDatePlanModal";
import { AddOrEditMatterLitigationDatePlanDateModal } from "@/components/features/settings/defaults/matter-litigation-date-plans/AddOrEditMatterLitigationDatePlanDateModal";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Skeleton,
  Text,
} from "@/components/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterLitigationDatePlanDatesQueryOptions } from "@/lib/data/react-query/matters/matter-litigation-date-plan-date";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  type MatterLitigationDatePlan,
  type MatterLitigationDatePlanDate,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { ErrorBoundary } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, ChevronRight, Pencil, Plus } from "lucide-react";
import { sortBy } from "lodash";
import pluralize from "pluralize";
import { useEffect, useMemo, useState } from "react";

const resolveLitigationDateTypeLabel = (
  dateTypeLookup: Map<string, string>,
  typeId?: string,
) => {
  if (!typeId) return "-";
  return dateTypeLookup.get(typeId) ?? typeId;
};

const formatDeadlineDays = (deadline?: number) => {
  const normalizedDeadline = Number(deadline ?? 0);
  return pluralize("day", Math.abs(normalizedDeadline), true);
};

const deadlineDirectionBadgeClassName = (deadline?: number) =>
  Number(deadline ?? 0) < 0
    ? "border-amber-200 bg-amber-50 text-amber-700"
    : "border-blue-200 bg-blue-50 text-blue-700";

const deadlineDirectionLabel = (deadline?: number) =>
  Number(deadline ?? 0) < 0 ? "before" : "after";

interface LitigationPlanAccordionItemProps {
  plan: MatterLitigationDatePlan;
  isExpanded: boolean;
  hasCreatePermission: boolean;
  hasUpdatePermission: boolean;
  litigationDateTypeLookup: Map<string, string>;
  onEditPlan: (plan: MatterLitigationDatePlan) => void;
  onAddPlanDate: () => void;
  onEditPlanDate: (planDate: MatterLitigationDatePlanDate) => void;
}

function LitigationPlanAccordionItem({
  plan,
  isExpanded,
  hasCreatePermission,
  hasUpdatePermission,
  litigationDateTypeLookup,
  onEditPlan,
  onAddPlanDate,
  onEditPlanDate,
}: LitigationPlanAccordionItemProps) {
  const [hasLoadedPlanDates, setHasLoadedPlanDates] = useState(isExpanded);

  useEffect(() => {
    if (isExpanded) {
      setHasLoadedPlanDates(true);
    }
  }, [isExpanded]);

  const { data: planDatesData, isLoading: isPlanDatesLoading } = useQuery({
    ...retrieveMatterLitigationDatePlanDatesQueryOptions(plan.id),
    enabled: hasLoadedPlanDates,
  });

  const planDates = useMemo(
    () =>
      sortBy(planDatesData?.data ?? [], (planDate) =>
        resolveLitigationDateTypeLabel(
          litigationDateTypeLookup,
          planDate.typeId,
        ).toLowerCase(),
      ),
    [planDatesData?.data, litigationDateTypeLookup],
  );

  return (
    <AccordionItem value={plan.id} className="border-0">
      <Card className="border-border/60 overflow-hidden shadow-sm">
        <CardContent className="p-0">
          <div className="group hover:bg-light-primary/50 flex w-full cursor-pointer items-center gap-3 px-4 py-5 transition-colors sm:px-6">
            <AccordionTrigger
              className="min-w-0 flex-1 cursor-pointer items-center py-0 hover:bg-transparent hover:no-underline [&[data-state=open]_.litigation-plan-chevron]:rotate-90"
              icon={<></>}
            >
              <div className="flex min-w-0 flex-1 items-center gap-3 pr-2 text-left">
                <ChevronRight
                  className="litigation-plan-chevron text-muted-foreground h-4 w-4 shrink-0 self-center transition-transform duration-200"
                  aria-hidden
                />
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-blue-50"
                  aria-hidden
                >
                  <CalendarDays className="h-4 w-4 text-blue-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <Text variant="body">{plan.label}</Text>
                  {plan.description ? (
                    <Text variant="caption" className="mt-0.5">
                      {plan.description}
                    </Text>
                  ) : null}
                  {plan.incidentState?.length ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {plan.incidentState.map((state) => (
                        <Badge
                          key={state}
                          variant="outline"
                          className="font-normal"
                        >
                          {state}
                        </Badge>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </AccordionTrigger>

            {hasUpdatePermission ? (
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground focus-visible:text-foreground hover:bg-muted/70 focus-visible:bg-muted/70 ml-auto inline-flex h-9 w-9 shrink-0 items-center justify-center self-start rounded-md opacity-100 transition-[opacity,color,background-color] sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onEditPlan(plan);
                }}
              >
                <span className="sr-only">Edit {plan.label}</span>
                <Pencil className="h-4 w-4" aria-hidden />
              </button>
            ) : null}
          </div>

          <AccordionContent className="px-0 pb-0">
            <div className="border-border/60 divide-border/60 divide-y border-t">
              {isExpanded && isPlanDatesLoading ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="px-4 py-5 sm:px-6">
                    <Skeleton className="h-12 w-full rounded-md" />
                  </div>
                ))
              ) : planDates.length > 0 ? (
                planDates.map((planDate) => {
                  const deadline = Number(planDate.deadline);
                  const triggerLabel = resolveLitigationDateTypeLabel(
                    litigationDateTypeLookup,
                    planDate.triggerId,
                  );
                  const dateTypeLabel = resolveLitigationDateTypeLabel(
                    litigationDateTypeLookup,
                    planDate.typeId,
                  );

                  const content = (
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <Text variant="body">{dateTypeLabel}</Text>
                        <Text
                          variant="subHeading"
                          className="mt-0.5 flex flex-wrap items-center gap-2"
                        >
                          <span>{formatDeadlineDays(deadline)}</span>
                          <span aria-hidden>→</span>
                          <Badge
                            variant="outline"
                            className={cn(
                              "rounded-full px-2 py-0 text-xs font-medium",
                              deadlineDirectionBadgeClassName(deadline),
                            )}
                          >
                            {deadlineDirectionLabel(deadline)}
                          </Badge>
                          <span className="min-w-0 truncate">
                            {triggerLabel}
                          </span>
                        </Text>
                      </div>
                      {hasUpdatePermission ? (
                        <span className="text-muted-foreground inline-flex h-9 w-9 shrink-0 items-center justify-center opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-visible:opacity-100">
                          <span className="sr-only">Edit {dateTypeLabel}</span>
                          <Pencil className="h-4 w-4" aria-hidden />
                        </span>
                      ) : null}
                    </div>
                  );

                  return hasUpdatePermission ? (
                    <button
                      key={planDate.id}
                      type="button"
                      className="bg-muted/20 group hover:bg-muted/50 focus-visible:bg-muted/50 flex w-full px-4 py-5 text-left transition-colors focus-visible:outline-none sm:px-6"
                      onClick={() => {
                        onEditPlanDate(planDate);
                      }}
                    >
                      {content}
                    </button>
                  ) : (
                    <div
                      key={planDate.id}
                      className="bg-muted/20 px-4 py-5 sm:px-6"
                    >
                      {content}
                    </div>
                  );
                })
              ) : (
                <div className="flex flex-col items-center gap-4 px-4 py-8 text-center sm:px-6">
                  <Text variant="subHeading">
                    No plan dates have been created for this plan.
                  </Text>
                  {hasCreatePermission ? (
                    <Button type="button" onClick={onAddPlanDate}>
                      <Plus className="h-4 w-4" aria-hidden />
                      Add date
                    </Button>
                  ) : null}
                </div>
              )}

              {hasCreatePermission && planDates.length > 0 ? (
                <button
                  type="button"
                  className="hover:bg-muted/50 focus-visible:bg-muted/50 flex w-full items-center gap-2 px-4 py-4 text-left transition-colors focus-visible:outline-none sm:px-6"
                  onClick={onAddPlanDate}
                >
                  <Plus
                    className="text-muted-foreground h-4 w-4 shrink-0"
                    aria-hidden
                  />
                  <Text as="span" variant="subHeading">
                    Add date
                  </Text>
                </button>
              ) : null}
            </div>
          </AccordionContent>
        </CardContent>
      </Card>
    </AccordionItem>
  );
}

function MatterLitigationDatePlansTableDangerous({
  isAddingPlan,
  setIsAddingPlan,
}: {
  isAddingPlan: boolean;
  setIsAddingPlan: (open: boolean) => void;
}) {
  const { hasActionPermission } = useUser();

  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLitigationDatePlan,
    ACTION_PERMISSION_VALUES.create,
  );
  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLitigationDatePlan,
    ACTION_PERMISSION_VALUES.update,
  );

  const [activeMatterLitigationDatePlan, setActiveMatterLitigationDatePlan] =
    useState<MatterLitigationDatePlan>();
  const [isPlanSlideoverOpen, setIsPlanSlideoverOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(false);
  const [expandedPlanId, setExpandedPlanId] = useState<string | undefined>();
  const [activePlanDate, setActivePlanDate] =
    useState<MatterLitigationDatePlanDate>();
  const [isAddingPlanDate, setIsAddingPlanDate] = useState(false);
  const [isEditingPlanDate, setIsEditingPlanDate] = useState(false);

  const {
    data: matterSettingsData,
    isLoading: isMatterSettingsLoading,
    isError: isMatterSettingsError,
  } = useQuery(retrieveMatterSettingsQueryOptions());

  const matterLitigationDatePlans = useMemo(
    () =>
      sortBy(matterSettingsData?.data.litigationDatePlans ?? [], (plan) =>
        plan.label.toLowerCase(),
      ),
    [matterSettingsData?.data.litigationDatePlans],
  );

  const litigationDateTypeLookup = useMemo(
    () =>
      new Map(
        (matterSettingsData?.data.litigationDateTypes ?? []).map((type) => [
          type.id,
          type.label,
        ]),
      ),
    [matterSettingsData?.data.litigationDateTypes],
  );

  useEffect(() => {
    if (!expandedPlanId) {
      return;
    }

    const expandedPlanStillExists = matterLitigationDatePlans.some(
      (plan) => plan.id === expandedPlanId,
    );

    if (!expandedPlanStillExists) {
      setExpandedPlanId(undefined);
    }
  }, [expandedPlanId, matterLitigationDatePlans]);

  const expandedPlan = useMemo(
    () => matterLitigationDatePlans.find((plan) => plan.id === expandedPlanId),
    [expandedPlanId, matterLitigationDatePlans],
  );

  const activePlanIdForSlideover = expandedPlan?.id ?? activePlanDate?.id;

  const { data: activePlanDatesData } = useQuery(
    retrieveMatterLitigationDatePlanDatesQueryOptions(
      activePlanIdForSlideover ?? "",
    ),
  );

  const activePlanDates = useMemo(
    () => activePlanDatesData?.data ?? [],
    [activePlanDatesData?.data],
  );

  useEffect(() => {
    if (isAddingPlan) {
      setEditingPlan(false);
      setActiveMatterLitigationDatePlan(undefined);
      setIsPlanSlideoverOpen(true);
    }
  }, [isAddingPlan]);

  const openEditPlan = (plan: MatterLitigationDatePlan) => {
    setEditingPlan(true);
    setActiveMatterLitigationDatePlan(plan);
    setIsPlanSlideoverOpen(true);
  };

  if (isMatterSettingsError) {
    return <Text variant="subHeading">{serverErrorFallbackText}</Text>;
  }

  return (
    <>
      <div className="w-full">
        {isMatterSettingsLoading ? (
          <div className="flex flex-col gap-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <Card key={index} className="border-border/60 shadow-sm">
                <CardContent className="p-4 sm:p-6">
                  <div className="h-24 w-full animate-pulse rounded-md bg-neutral-100" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : matterLitigationDatePlans.length === 0 ? (
          <Card className="border-border/60 border-dashed shadow-sm">
            <CardContent className="py-10 text-center">
              <Text variant="body">No litigation plans yet</Text>
              <Text variant="subHeading" className="mt-0.5">
                Add a plan to create reusable litigation timelines for your
                matters.
              </Text>
            </CardContent>
          </Card>
        ) : (
          <Accordion
            type="single"
            collapsible
            onValueChange={(value) => {
              setExpandedPlanId(value || undefined);
            }}
            className="flex w-full flex-col gap-4"
          >
            {matterLitigationDatePlans.map((plan) => (
              <LitigationPlanAccordionItem
                key={plan.id}
                plan={plan}
                isExpanded={expandedPlanId === plan.id}
                hasCreatePermission={hasCreatePermission}
                hasUpdatePermission={hasUpdatePermission}
                litigationDateTypeLookup={litigationDateTypeLookup}
                onEditPlan={openEditPlan}
                onAddPlanDate={() => {
                  setExpandedPlanId(plan.id);
                  setActivePlanDate(undefined);
                  setIsEditingPlanDate(false);
                  setIsAddingPlanDate(true);
                }}
                onEditPlanDate={(planDate) => {
                  setExpandedPlanId(plan.id);
                  setActivePlanDate(planDate);
                  setIsEditingPlanDate(true);
                  setIsAddingPlanDate(false);
                }}
              />
            ))}
          </Accordion>
        )}
      </div>

      <AddOrEditMatterLitigationDatePlanModal
        editing={editingPlan}
        open={isPlanSlideoverOpen}
        setOpen={(open) => {
          setIsPlanSlideoverOpen(open);
          if (!open) {
            setIsAddingPlan(false);
          }
        }}
        activeMatterLitigationDatePlan={activeMatterLitigationDatePlan}
        isReadOnly={!hasUpdatePermission}
        onTransitionToEdit={(plan) => {
          setEditingPlan(true);
          setActiveMatterLitigationDatePlan(plan);
          setExpandedPlanId(plan.id);
        }}
      />

      {isAddingPlanDate && expandedPlan ? (
        <AddOrEditMatterLitigationDatePlanDateModal
          open={isAddingPlanDate}
          setOpen={setIsAddingPlanDate}
          planId={expandedPlan.id}
          editing={false}
          isReadOnly={!hasUpdatePermission}
          litigationDateTypes={
            matterSettingsData?.data.litigationDateTypes ?? []
          }
          isLitigationDateTypesLoading={isMatterSettingsLoading}
          planDates={activePlanDates}
        />
      ) : null}

      {isEditingPlanDate && activePlanDate && expandedPlan ? (
        <AddOrEditMatterLitigationDatePlanDateModal
          open={isEditingPlanDate}
          setOpen={setIsEditingPlanDate}
          planId={expandedPlan.id}
          editing
          activeDate={activePlanDate}
          isReadOnly={!hasUpdatePermission}
          litigationDateTypes={
            matterSettingsData?.data.litigationDateTypes ?? []
          }
          isLitigationDateTypesLoading={isMatterSettingsLoading}
          planDates={activePlanDates}
        />
      ) : null}
    </>
  );
}

export function MatterLitigationDatePlansTable({
  isAddingPlan,
  setIsAddingPlan,
}: {
  isAddingPlan: boolean;
  setIsAddingPlan: (open: boolean) => void;
}) {
  return (
    <ErrorBoundary
      fallback={<Text variant="subHeading">{serverErrorFallbackText}</Text>}
    >
      <MatterLitigationDatePlansTableDangerous
        isAddingPlan={isAddingPlan}
        setIsAddingPlan={setIsAddingPlan}
      />
    </ErrorBoundary>
  );
}
